import { Filters } from '../ui/pages/onyxai-insights/OnyxAIInsights'
import { useQuery, useQueryClient } from 'react-query'
import axios from '../lib/axios'
import { buildInsightsSearchBody, INSIGHTS_V3_BASE, InsightsSearchParams } from '../lib/insightsApi'
import dayjs from 'dayjs'
import { dateFormat } from '../lib/utils'
import { LabelOption } from '../ui/pages/library/components/LibrarySelectLabelField'

/* A Data Source option stands for an integration, which a tenant may run several instances of. */
export interface DataSourceOption extends LabelOption {
  ids?: string[]
}

export function useSearchInsights({
  value,
  filters,
  reset,
  enabled,
  dataSourceOptions,
  cpiOptions,
  checkReadStatus,
  customTimePeriodValue,
  lastChanged,
  basePath
}: {
  value: string
  filters: Filters
  reset: boolean
  keepPreviousData?: boolean
  enabled?: boolean
  staleTime?: any
  changeReadStatus: (integrationIds: string[], read: boolean) => Promise<void>
  initialLoad?: boolean
  checkReadStatus: (integrationIds: string[]) => Promise<void>
  dataSourceOptions: DataSourceOption[]
  cpiOptions: LabelOption[]
  customTimePeriodValue: string
  lastChanged: string
  basePath?: string
}) {
  const base = basePath ?? INSIGHTS_V3_BASE
  const url = `${base}/search`
  const { time, subType, dataSources, cpis, severity, sortDirection, sortBy } = filters
  const searchParams: InsightsSearchParams = {
    term: value
  }

  if (!reset) {
    if (time.length) {
      if (time === 'Today') {
        searchParams.dateFrom = dayjs().format(dateFormat.customLabel)
        searchParams.dateTo = dayjs().format(dateFormat.customLabel)
      }
      if (time === 'Last Month') {
        const today = dayjs().format('YYYY-MM-DD')
        const past30 = dayjs(today).subtract(30, 'days')
        searchParams.dateFrom = dayjs(past30).format('YYYY-MM-DD')
        searchParams.dateTo = today
      }
      if (time === 'Custom') {
        const shortMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
        const parts = customTimePeriodValue.split('-')
        const sub1 = parts[0].split(" '")
        const sub2 = parts[1].split(" '")
        const startMonth = String(shortMonths.findIndex((n) => n === sub1[0]) + 1).padStart(2, '0')
        const startYear = sub1.length > 1 ? `20${sub1[1]}` : `20${sub2[1]}`
        const endMonth = String(shortMonths.findIndex((n) => n === sub2[0]) + 1).padStart(2, '0')
        const endYear = `20${sub2[1]}`
        const daysInEndMonth = dayjs(`${endYear}-${endMonth}-01`).daysInMonth()
        searchParams.dateFrom = dayjs(`${startYear}-${startMonth}-01`).format(dateFormat.customLabel)
        searchParams.dateTo = dayjs(`${endYear}-${endMonth}-${daysInEndMonth}`).format(dateFormat.customLabel)
      }
    }
    if (subType.length && !(subType.length === 1 && subType[0] === 'all')) {
      searchParams.types = subType
    }
    if (dataSources.length) {
      const selected = dataSourceOptions.filter((n) => dataSources.includes(n.label))
      // An option covers every instance of its integration, so match on all of their ids.
      searchParams.instanceIds = selected.flatMap((c) => c.ids ?? [c.id]).filter((id): id is string => !!id)
    }
    if (cpis.length) {
      searchParams.cpis = cpiOptions.filter((c) => cpis.includes(c.label)).map((n) => n.name)
    }
    if (severity.length && !(severity.length === 1 && severity[0] === 'all')) {
      searchParams.sections = severity.map((c) => c.toLowerCase())
    }
    if (sortDirection) {
      searchParams.sortDirection = sortDirection
    }
    if (sortBy) {
      searchParams.sortBy = sortBy
    }
  }

  const requestBody = buildInsightsSearchBody(searchParams, base)
  const queryClient = useQueryClient()

  const queryKey = ['insights', basePath, lastChanged, { ...requestBody }]
  // Keep the previous result on screen while a new one loads. Dropping it mid-flight is
  // what let the page collapse into a full-height spinner on every filter change.
  const { data, isFetching, status } = useQuery(queryKey, () => axios.post(url, requestBody), {
    keepPreviousData: true,
    initialData: undefined,
    enabled,
    staleTime: Infinity
  })
  const ids: string[] = (data as any)?.data.data.map((n: any) => n.id) || []

  // Keyed by the ids it describes rather than by `lastChanged`, so a read/unread toggle can
  // patch this entry in place instead of refetching the list behind a loading state.
  const readStatusKey = ['insights-read-status', base, ids]

  const { data: withReadStatus, isFetching: isFetching2 } = useQuery(
    readStatusKey,
    async () => {
      if (!ids.length) return
      // Read state is decoration — if the lookup fails, still surface the insights as unread
      // rather than dropping the whole list into the "No Insights Found" empty state.
      let readIds: string[] = []
      try {
        const response: any = await checkReadStatus(ids)
        readIds = response?.readIds ?? []
      } catch (err) {
        console.error(err)
      }

      // v2 items carry `type` instead of `sub_type` and the category in `family`; alias both
      // so the rest of the FE keeps reading the legacy shape (top-level `type` = category).
      return (data as any)?.data.data.map((n: any) => ({
        ...n,
        sub_type: n.sub_type ?? n.type,
        type: n.family ?? n.type,
        read: readIds.includes(n.id)
      }))
    },
    {
      keepPreviousData: true,
      initialData: undefined,
      enabled: ids.length > 0,
      staleTime: Infinity
    }
  )

  // A search that matches nothing has no entry of its own, so `keepPreviousData` would
  // hold the last non-empty list here — surface the empty result instead.
  const insights = ids.length > 0 ? withReadStatus : undefined

  // Flips read flags in the cached list and returns the rollback for the snapshot it
  // replaced, so a rejected request restores the exact previous state.
  const applyReadStatus = (targetIds: string[], read: boolean) => {
    const previous = queryClient.getQueryData<any[]>(readStatusKey)
    queryClient.setQueryData<any>(readStatusKey, (prev: any) =>
      Array.isArray(prev) ? prev.map((n: any) => (targetIds.includes(n.id) ? { ...n, read } : n)) : prev
    )

    return () => queryClient.setQueryData<any>(readStatusKey, previous)
  }

  return {
    isFetching: isFetching,
    isFetching2: isFetching2,
    loading: isFetching || isFetching2,
    // The list has never resolved — distinct from a refetch running behind existing results.
    initialLoading: status === 'idle' || status === 'loading',
    data: insights,
    applyReadStatus
  }
}
